import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TooltipLayer from '../tooltips/TooltipLayer';
import CopyrightNotice from './CopyrightNotice';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission
}) => {
  const { isAuthenticated, user, hasPermission, isLoading, error } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's a connection or configuration issue
  if (error && !isAuthenticated) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4 max-w-md p-6">
          <div className="text-red-600 text-5xl">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800">Connection Error</h2>
          <p className="text-gray-600 text-center">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Send unauthenticated users to the landing page with the login popup,
    // not the old standalone /login page. After login the popup returns to
    // `from` (e.g. the tool whose session ran out).
    return <Navigate to="/?login=1" state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Tooltips and the copyright notice exist only on the logged-in tool pages,
  // never on the public site (landing page, login, password reset).
  // See tooltips/tips.js and CopyrightNotice.js.
  return (
    <>
      <TooltipLayer />
      <CopyrightNotice />
      {children}
    </>
  );
};

export default ProtectedRoute;
